import { AppShell as MantineAppShell, Box, Flex } from '@mantine/core';
import { useFavicon, useHover } from '@mantine/hooks';
import { memo, useRef } from 'react';
import { HitSlop } from 'src/common/components/HitSlop';
import { Outlet } from 'react-router-dom';
import { UsersProvider } from 'src/admin/users/context/UsersContext';
import { ClientsProvider } from 'src/clients/context/ClientsContext';
import { CurrentClientProvider } from 'src/clients/context/CurrentClientProvider';
import FactFindProvider from 'src/fact-find/FactFindProvider';
import AgendaProvider from 'src/meeting-agenda/AgendaProvider';
import MeetingNoteProvider from 'src/meeting-note/MeetingNoteProvider';
import SuitabilityProvider from 'src/suitability-report/SuitabilityProvider';
import { AppContainerProvider, useAppContainerContext } from './AppContainerContext';
import { AppContentPane } from './AppContentPane';
import { AppSidebar } from './AppSidebar';
import { DefaultSidebar } from './DefaultSidebar';
import { SidebarContent } from './SidebarContent';
import Header, { HEADER_HEIGHT } from './header/Header';
import { useDragAnimation } from './useDragAnimation';

function AppContainer() {
  useFavicon('https://public-afternoon-bucket.s3.eu-north-1.amazonaws.com/smallLogo.svg');

  return (
    <CurrentClientProvider>
      <ClientsProvider>
        <SuitabilityProvider>
          <FactFindProvider>
            <AgendaProvider>
              <MeetingNoteProvider>
                <UsersProvider>
                  <AppContainerProvider>
                    <AppContainerLayout />
                  </AppContainerProvider>
                </UsersProvider>
              </MeetingNoteProvider>
            </AgendaProvider>
          </FactFindProvider>
        </SuitabilityProvider>
      </ClientsProvider>
    </CurrentClientProvider>
  );
}

function AppContainerLayout() {
  const { sidebarContent, mode, setMode } = useAppContainerContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    handleMouseDown,
    shouldTransition,
    mainBgWidth,
    sidebarBgWidth,
    mainPaneWidth,
    sidebarPaneWidth,
  } = useDragAnimation({ mode, setMode, containerRef });
  const { hovered, ref: hoverRef } = useHover();

  return (
    <MantineAppShell
      header={{
        height: HEADER_HEIGHT,
      }}
    >
      <Header showChat={() => {}} isChatOpened={false} bg="gray.10" />
      <MantineAppShell.Main bg="gray.10">
        <Flex
          gap="xxxs"
          ref={containerRef}
          bg="gray.10"
          p="xxs"
          style={{
            height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          }}
        >
          <AppContentPane
            bgWidth={mainBgWidth}
            paneWidth={mainPaneWidth}
            shouldTransition={shouldTransition}
          >
            <Outlet />
          </AppContentPane>

          <HitSlop
            ref={hoverRef}
            amount={12}
            cursor="col-resize"
            zIndex={10}
            onMouseDown={handleMouseDown}
            style={{ alignSelf: 'center', flexShrink: 0 }}
          >
            <Box
              bg={hovered ? 'brand.5' : 'brand.6'}
              style={{
                width: 'var(--mantine-spacing-xxxs)',
                height: 'var(--mantine-spacing-xxl)',
                borderRadius: 'var(--mantine-spacing-xs)',
                transition: 'background-color 0.2s ease',
                pointerEvents: 'none',
              }}
            />
          </HitSlop>

          <AppSidebar
            bgWidth={sidebarBgWidth}
            paneWidth={sidebarPaneWidth}
            shouldTransition={shouldTransition}
          >
            {sidebarContent ?? <DefaultSidebar />}
          </AppSidebar>
        </Flex>
      </MantineAppShell.Main>
    </MantineAppShell>
  );
}

type AppContainerComponent = React.MemoExoticComponent<typeof AppContainer> & {
  SidebarContent: typeof SidebarContent;
};

const MemoizedAppContainer = memo(AppContainer) as AppContainerComponent;
MemoizedAppContainer.SidebarContent = SidebarContent;

export default MemoizedAppContainer;
